// src/AuthContext.js
import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';


const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [userType, setUserType] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isIdle, setIsIdle] = useState(false);
    const navigate = useNavigate();

    const idleTimer = useRef(null);
    const finalTimeout = useRef(null);

    const logout = useCallback(async () => {
        await supabase.auth.signOut();
        localStorage.removeItem('studentData'); // Limpiar también localStorage
        setUser(null);
        setUserData(null);
        setUserType(null);
        setIsIdle(false);
        clearTimeout(idleTimer.current);
        clearTimeout(finalTimeout.current);
        navigate('/'); // Redirigir al inicio después de cerrar sesión
    }, [navigate]);

    const resetIdleTimer = useCallback(() => {
        setIsIdle(false);
        clearTimeout(idleTimer.current);
        clearTimeout(finalTimeout.current);

        idleTimer.current = setTimeout(() => {
            setIsIdle(true);
            finalTimeout.current = setTimeout(logout, 60 * 1000); // 1 minuto de advertencia antes del logout
        }, 9 * 60 * 1000); // 9 minutos de inactividad
    }, [logout]);

    useEffect(() => {
        if (user) {
            const events = ['mousemove', 'keydown', 'click', 'scroll'];
            events.forEach(event => window.addEventListener(event, resetIdleTimer));
            resetIdleTimer();

            return () => {
                events.forEach(event => window.removeEventListener(event, resetIdleTimer));
                clearTimeout(idleTimer.current);
                clearTimeout(finalTimeout.current);
            };
        }
    }, [user, resetIdleTimer]);

    useEffect(() => {
        let isMounted = true; // Flag para evitar actualizaciones de estado en un componente desmontado

        const fetchUserData = async (authUser) => {
            if (!authUser) {
                if (isMounted) {
                    setUserData(null);
                    setUserType(null);
                    setLoading(false);
                }
                return;
            }
            
            if (isMounted) setLoading(true);

            try {
                const role = authUser.user_metadata?.role;

                if (role === 'student') {
                    const { data: student, error: studentError } = await supabase
                        .from('students')
                        .select(`*, schools (*)`)
                        .eq('auth_id', authUser.id)
                        .single();
                    if (studentError && studentError.code !== 'PGRST116') throw studentError;
                    
                    if (isMounted) {
                        setUserData(student || null);
                        setUserType(student ? 'student' : null);
                    }
                } else if (role === 'parent') {
                    // CORRECCIÓN: La tabla 'parents' se vincula por 'id', que debe ser igual al 'auth_id'.
                    const { data: parent, error: parentError } = await supabase
                        .from('parents')
                        .select(`*, students:parent_student_relations(students(*, schools(*)))`)
                        .eq('id', authUser.id)
                        .single();

                    if (parentError && parentError.code !== 'PGRST116') throw parentError;
                    
                    if (isMounted) {
                        if (parent) {
                            const parentWithStudents = {
                                ...parent,
                                students: parent.students.map(ps => ps.students).filter(Boolean)
                            };
                            setUserData(parentWithStudents);
                            setUserType('parent');
                        } else {
                            setUserData(null);
                            setUserType(null);
                        }
                    }
                } else {
                    // --- INICIO DE MODIFICACIÓN ---
                    // Si no es 'student' o 'parent', intentamos buscarlo en la tabla 'teachers'.
                    // Esto capturará cualquier otro rol (ej. 'staff') o si el metadata.role está vacío.
                    const { data: teacher, error: teacherError } = await supabase
                        .from('teachers') // Usamos la tabla 'teachers'
                        .select(`*, schools (*)`)
                        .eq('auth_id', authUser.id) // Vinculamos por 'auth_id'
                        .single();

                    if (teacherError && teacherError.code !== 'PGRST116') {
                        // Si hay un error QUE NO SEA "fila no encontrada", lo registramos.
                        console.error("Error fetching teacher data:", teacherError);
                    }
                    
                    if (isMounted) {
                        if (teacher) {
                            // ¡Encontrado! Establecemos el usuario como 'teacher'.
                            setUserData(teacher);
                            setUserType('teacher'); // <--- ESTO CORRIGE EL ERROR
                        } else {
                            // No es student, no es parent, y no es teacher. Ahora sí, es 'desconocido'.
                            setUserData(null);
                            setUserType(null);
                        }
                    }
                    // --- FIN DE MODIFICACIÓN ---
                }
            } catch (error) {
                console.error("Error fetching user data:", error.message);
                if (isMounted) {
                    setUserData(null);
                    setUserType(null);
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        const studentDataFromStorage = localStorage.getItem('studentData');
        if (studentDataFromStorage) {
            try {
                const parsedData = JSON.parse(studentDataFromStorage);
                setUserData(parsedData);
                setUserType('student');
                setUser({ isLocalStorage: true, ...parsedData });
            } catch (e) {
                localStorage.removeItem('studentData');
            }
        }

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (event, session) => {
                if (!isMounted) return;
                
                const authUser = session?.user ?? null;
                setUser(authUser);

                if (event === 'SIGNED_OUT') {
                    localStorage.removeItem('studentData');
                    setUserData(null);
                    setUserType(null);
                    setLoading(false);
                } else if (authUser) {
                     localStorage.removeItem('studentData');
                     fetchUserData(authUser);
                } else {
                     setLoading(false);
                }
            }
        );
        
        // Carga inicial si no hay sesión ni datos de localStorage
        const checkInitialSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
             if (!isMounted) return;
            if (!session && !studentDataFromStorage) {
                setLoading(false);
            }
        }
        checkInitialSession();


        return () => {
            isMounted = false;
            subscription.unsubscribe();
        };
    }, []);

    const loginStudent = (studentData) => {
        setUserData(studentData);
        setUserType('student');
        setUser({ isLocalStorage: true, ...studentData }); // Simula un objeto de usuario
        localStorage.setItem('studentData', JSON.stringify(studentData));
    };

    const value = {
        user,
        userData,
        userType,
        loading,
        isIdle,
        resetIdleTimer,
        supabase,
        login: (email, password) => supabase.auth.signInWithPassword({ email, password }),
        loginStudent,
        logout,
    };
    
    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};